import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'
import { Building, Compass, Link, Table, type LucideIcon } from 'lucide-react'
import { Card, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'
import { FieldLabel } from '@/components/FieldLabel'
import { ObjectPicker } from '@/components/ObjectPicker'
import { AuthService } from '@/services/core/auth'
import type { ResourceUsage, UsageParam } from '@/services/core/listSearch'
import { SitesApi } from '@/services/sites/sitesApi'
import { AdUnitsApi } from '@/services/adUnits/adUnitsApi'
import { EarningsAccountsApi } from '@/services/earnings/earningsAccountsApi'

type UsageKey = keyof ResourceUsage
type UsageRef = ResourceUsage[UsageKey][number]

interface ResourceUsageCardProps {
  /** The list filter that finds what uses the resource. */
  usageParam: UsageParam
  resourceId: string
  /** Makes the lists editable; the page loads the usage and saves the changes. */
  edit?: {
    value: ResourceUsage
    onChange: (value: ResourceUsage) => void
    /** Tooltip on each list, e.g. what adding does to the object. */
    help?: string
  }
}

interface UsageGroupProps {
  title: string
  icon: LucideIcon
  items: UsageRef[]
  href: (id: string) => string
}

function UsageGroup({ title, icon: Icon, items, href }: UsageGroupProps) {
  const { t } = useTranslation()
  return (
    <div>
      <div className="px-4 pt-2.5 pb-1 text-[11px] font-semibold text-(--ad-text-faint)">
        {title} ({items.length})
      </div>
      {items.length === 0 && (
        <div className="px-4 py-1.5 text-[12.5px] text-(--ad-text-muted)">
          {t('resourceUsage.none')}
        </div>
      )}
      {items.map((item) => (
        <RouterLink
          key={item.id}
          to={href(item.id)}
          className="ad-usage-row flex items-center gap-2 px-4 py-1.5 text-[13px] text-(--ad-text)"
        >
          <Icon className="size-3 shrink-0 text-(--ad-text-muted)" />
          {item.name}
        </RouterLink>
      ))}
    </div>
  )
}

const ref = ({ id, name }: UsageRef) => ({ id, name })

/** The publishers, sites and ad units that use a blocklist or ruleset. */
export function ResourceUsageCard({ usageParam, resourceId, edit }: ResourceUsageCardProps) {
  const { t } = useTranslation()
  const enabled = !!resourceId && !edit && AuthService.isAuthenticated()

  const { data: publishers = [] } = useQuery({
    queryKey: [`${usageParam}-usage-publishers`, resourceId],
    queryFn: () => EarningsAccountsApi.getByUsage(usageParam, resourceId),
    enabled,
    staleTime: 0,
  })

  const { data: sites = [] } = useQuery({
    queryKey: [`${usageParam}-usage-sites`, resourceId],
    queryFn: () => SitesApi.getByUsage(usageParam, resourceId),
    enabled,
    staleTime: 0,
  })

  const { data: adUnits = [] } = useQuery({
    queryKey: [`${usageParam}-usage-adunits`, resourceId],
    queryFn: () => AdUnitsApi.getByUsage(usageParam, resourceId),
    enabled,
    staleTime: 0,
  })

  const groups: {
    key: UsageKey
    addLabel: string
    icon: LucideIcon
    href: (id: string) => string
    search: (term: string) => Promise<UsageRef[]>
  }[] = [
    {
      key: 'publishers',
      addLabel: t('resourceUsage.addPublishers'),
      icon: Building,
      href: (id) => `/publishing/earnings/${id}`,
      search: async (term) => (await EarningsAccountsApi.getAll(term, 'ACTIVE')).map(ref),
    },
    {
      key: 'sites',
      addLabel: t('resourceUsage.addSites'),
      icon: Compass,
      href: (id) => `/publishing/sites/${id}`,
      search: async (term) =>
        (await SitesApi.getPage({ search: term, pageSize: 50, status: 'ACTIVE' })).results.map(ref),
    },
    {
      key: 'adUnits',
      addLabel: t('resourceUsage.addAdUnits'),
      icon: Table,
      href: (id) => `/publishing/ad-units/${id}`,
      search: async (term) =>
        (await AdUnitsApi.getPage({ search: term, pageSize: 50, status: 'ACTIVE' })).results.map(
          ref
        ),
    },
  ]
  const loaded: ResourceUsage = { publishers, sites, adUnits }

  return (
    <Card tone="insight" className="min-w-0 flex-1">
      <CardHeader>
        <CardTitle>
          <CardIcon icon={Link} />
          {t('resourceUsage.title')}
        </CardTitle>
      </CardHeader>
      {edit ? (
        <CardContent>
          <div className="field-grid">
            {groups.map((group) => (
              <div key={group.key} className="p-field full">
                <FieldLabel tooltip={edit.help}>
                  {t(`resourceUsage.${group.key}`)} ({edit.value[group.key].length})
                </FieldLabel>
                <ObjectPicker
                  queryKey={`${usageParam}-usage-picker-${group.key}`}
                  search={group.search}
                  selected={edit.value[group.key]}
                  onChange={(items) =>
                    edit.onChange({ ...edit.value, [group.key]: items.map(ref) })
                  }
                  href={(item) => group.href(item.id)}
                  addLabel={group.addLabel}
                  emptyText={t('resourceUsage.none')}
                />
              </div>
            ))}
          </div>
        </CardContent>
      ) : (
        <CardContent className="p-0 pb-2">
          {groups.map((group) => (
            <UsageGroup
              key={group.key}
              title={t(`resourceUsage.${group.key}`)}
              icon={group.icon}
              items={loaded[group.key]}
              href={group.href}
            />
          ))}
        </CardContent>
      )}
    </Card>
  )
}
